import React, { useEffect, useRef, useState } from 'react';
import { motion, useAnimation, AnimatePresence } from 'framer-motion';

interface NavigationProgressBarProps {
  loading: boolean;
}

const NavigationProgressBar: React.FC<NavigationProgressBarProps> = ({
  loading,
}) => {
  // Acepta la prop loading
  const barAnimation = useAnimation();
  const [isAnimating, setIsAnimating] = useState(false);
  const animationTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const isMobile =
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent
    );

  const startProgress = async () => {
    if (animationTimeoutRef.current) {
      clearTimeout(animationTimeoutRef.current);
    }

    setIsAnimating(true);

    await barAnimation.set({ width: '0%', opacity: 1 });
    await barAnimation.start({
      width: '100%',
      transition: { duration: 0.4, ease: 'easeOut' },
    });
  };

  const finishProgress = async () => {
    await barAnimation.start({
      opacity: 0,
      transition: { duration: 0.3 },
    });
    setIsAnimating(false);
  };

  useEffect(() => {
    if (loading) {
      // Iniciar la animación SOLO cuando la prop 'loading' es true
      startProgress();
    } else if (isAnimating) {
      // Si estaba animando y 'loading' es false, finalizar
      const timeoutId = setTimeout(() => {
        finishProgress();
      }, 200); // Pequeño delay antes de desaparecer

      return () => clearTimeout(timeoutId);
    }
  }, [loading, isAnimating]); // Depende de 'loading' y 'isAnimating'

  if (isMobile) {
    return null;
  }

  return (
    <AnimatePresence>
      {loading && (
        <motion.div
          className="fixed top-0 left-0 right-0 z-[9999] h-1.5 pointer-events-none bg-transparent overflow-hidden"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div
            className="h-full bg-gradient-to-r from-yellow-500 to-red-500 shadow-sm"
            animate={barAnimation}
            initial={{ width: '0%', opacity: 1 }}
            style={{ originX: 0 }}
          />
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default NavigationProgressBar;
